import { Auth0Client } from "@auth0/auth0-spa-js";
import { reactive } from "vue";
import { getCookie } from "./GlobalServices";

// Auth0の設定
const domain = process.env.VUE_APP_AUTH0_DOMAIN;
const clientId = process.env.VUE_APP_AUTH0_CLIENT_ID;
const audience = process.env.VUE_APP_AUTH0_AUDIENCE;
const redirectUri = process.env.VUE_APP_AUTH0_CALLBACK_URL;

// 認証状態を保持するリアクティブオブジェクト
export const authState = reactive({
  loading: true,
  isAuthenticated: false,
  user: null,
  auth0Client: null,
  popupOpen: false,
  error: null,
  token: null,
  idToken: null,
});

/**
 * Auth0クライアントの初期化
 */
export const initAuth = async () => {
  try {
    // Auth0クライアントの作成
    authState.auth0Client = new Auth0Client({
      domain,
      clientId, // v2ではclient_idではなくclientIdを使用
      authorizationParams: {
        redirect_uri: redirectUri,
        audience,
        scope: "openid profile email",
      },
      cacheLocation: "localstorage",
    });

    // URLにcodeとstateパラメータがある場合（コールバック時）
    const query = window.location.search;
    if (query.includes("code=") && query.includes("state=")) {
      // Auth0からのリダイレクト後の処理
      await authState.auth0Client.handleRedirectCallback();

      // クエリパラメータをクリア
      window.history.replaceState({}, document.title, window.location.pathname);

      // 明示的なAuth0ログインが完了したため、残留している契約社員トークンを削除する
      // （下の契約社員セッションガードより先に消さないと、社員ログイン成功なのに
      //   未認証扱いになってしまう）
      localStorage.removeItem("contractor_token");
      localStorage.removeItem("contractor_email");
    }

    // 認証状態の確認
    // 契約社員セッションが有効な間はAuth0セッションをサイレント復元しない
    // （復元するとDSR_ACCESS_TOKENが再保存され、契約社員トークンと併存して
    //   どちらの身元でAPIを呼ぶかが曖昧になるため）
    const hasContractorSession = !!localStorage.getItem("contractor_token");
    const isAuthenticated =
      !hasContractorSession && (await authState.auth0Client.isAuthenticated());
    authState.isAuthenticated = isAuthenticated;

    if (isAuthenticated) {
      // ユーザー情報の取得
      authState.user = await authState.auth0Client.getUser();

      // アクセストークンの取得
      authState.token = await authState.auth0Client.getTokenSilently();

      // IDトークンの取得
      try {
        const idTokenClaims = await authState.auth0Client.getIdTokenClaims();
        if (idTokenClaims && idTokenClaims.__raw) {
          authState.idToken = idTokenClaims.__raw;
          // localStorageにIDトークンを保存
          localStorage.setItem("DSR_ACCESS_TOKEN", idTokenClaims.__raw);
        }
      } catch (idTokenError) {
        console.error("IDトークン取得エラー:", idTokenError);
      }
    }
  } catch (error) {
    authState.error = error;
    console.error("Auth0の初期化エラー:", error);
  } finally {
    authState.loading = false;
  }
};

/**
 * ログイン処理
 */
export const login = async () => {
  try {
    if (!authState.auth0Client) {
      // Auth0クライアントが初期化されていない場合は初期化
      console.log("Auth0クライアントが初期化されていないため初期化します");
      await initAuth();
    }

    await authState.auth0Client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: redirectUri,
      },
    });
  } catch (error) {
    console.error("ログインエラー:", error);
    authState.error = error;
  }
};

/**
 * Auth0のlocalStorageキャッシュをクリアする
 * Auth0 SPA SDKは @@auth0spajs@@ プレフィックスでトークンを保存する
 */
const clearAuth0Cache = () => {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("@@auth0spajs@@")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

/**
 * Auth0セッション（localStorageのトークン・キャッシュとauthState）をクリアする
 * 契約社員としてサインインする際にも呼び出し、Auth0セッションとの併存を防ぐ
 */
export const clearAuth0Session = () => {
  // localStorageからIDトークンを削除
  localStorage.removeItem("DSR_ACCESS_TOKEN");

  // Auth0のlocalStorageキャッシュをクリア（サイレント復元を防ぐ）
  clearAuth0Cache();

  // authStateをリセット
  authState.isAuthenticated = false;
  authState.user = null;
  authState.token = null;
  authState.idToken = null;
  authState.error = null;
};

/**
 * ログアウト処理
 */
export const logout = async () => {
  try {
    // 既存のトークンクッキーをクリア
    document.cookie = "token=code=;expires=Thu, 01 Jan 1970 00:00:01 GMT;";

    // 契約社員用のトークンも削除（別ユーザーとして再ログインする場合に備えて）
    localStorage.removeItem("contractor_token");
    localStorage.removeItem("contractor_email");

    // Auth0セッションをクリア（別ユーザーとして再ログインする場合に備えて）
    clearAuth0Session();

    if (!authState.auth0Client) {
      // Auth0クライアントが初期化されていない場合は初期化
      await initAuth();
    }

    // Auth0からログアウト
    await authState.auth0Client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  } catch (error) {
    console.error("ログアウトエラー:", error);
    authState.error = error;
  }
};

/**
 * アクセストークンの取得
 */
export const getToken = async () => {
  // Auth0認証が有効な場合はAuth0のトークンを返す
  if (authState.isAuthenticated && authState.auth0Client) {
    try {
      return await authState.auth0Client.getTokenSilently();
    } catch (error) {
      console.error("トークン取得エラー:", error);
      return null;
    }
  }

  // 従来の認証方法の場合はクッキーからトークンを返す
  return getCookie("token");
};

/**
 * IDトークンの取得
 */
export const getIdToken = async () => {
  // localStorageからIDトークンを取得
  const storedIdToken = localStorage.getItem("DSR_ACCESS_TOKEN");
  if (storedIdToken) {
    return storedIdToken;
  }

  // localStorageにない場合はAuth0から取得
  if (authState.isAuthenticated && authState.auth0Client) {
    try {
      const idTokenClaims = await authState.auth0Client.getIdTokenClaims();
      if (idTokenClaims && idTokenClaims.__raw) {
        // localStorageに保存
        localStorage.setItem("DSR_ACCESS_TOKEN", idTokenClaims.__raw);
        return idTokenClaims.__raw;
      }
    } catch (error) {
      console.error("IDトークン取得エラー:", error);
    }
  }

  return null;
};

export default {
  initAuth,
  login,
  logout,
  clearAuth0Session,
  getToken,
  getIdToken,
  authState,
};
